//@flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import { getCatalogs as getCatalogsAction } from 'state/commands/catalogCommands';
import {
  addOrUpdateMappingEntries,
  removeMappingEntry,
  getMappings as getMappingsCommand,
  saveMapping,
} from 'state/commands/dirSvcCommands';
import { getRoles as getRolesAction } from 'state/commands/roleCommands';
import { actions as dirSvcActions } from 'state/reducers/dirSvcReducer';
import {
  getCatalogs,
  getDirectoryServiceMappings,
  getDirectoryServiceDestination,
  getDirectoryServiceQueryType,
  getIsDirectoryServiceMappingsDirty,
  getRoles,
} from 'state/selectors';
import type {
  Catalog,
  DirSvcMapping,
  DirSvcMappingEntry,
  Dispatch,
  Role,
} from 'state/types';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import localizedStrings from 'util/localizedStrings';
import LabeledField from 'view/Forms/LabeledField';
import {
  ALL_CATALOGS_GUID,
  getMappingEntriesForDestination,
  getUserName,
  NO_CATALOGS_GUID,
  QUERY_TYPE_NAME,
} from 'util/dirSvcUtils';
import DestinationNode from './DestinationNode';

const { setDestination: setDestinationAction } = dirSvcActions;

type Props = {
  addOrUpdateMappingEntries: typeof addOrUpdateMappingEntries,
  catalogs: Catalog[],
  destination: string,
  getCatalogsAction: typeof getCatalogsAction,
  getMappingsCommand: typeof getMappingsCommand,
  getRolesAction: typeof getRolesAction,
  intl: IntlShape,
  isDirty: boolean,
  mappings: DirSvcMapping[],
  queryType: Object,
  removeMappingEntry: typeof removeMappingEntry,
  roles: Role[],
  saveMapping: typeof saveMapping,
  setDestinationAction: typeof setDestinationAction,
};

type State = {
  hasDeletes: boolean,
  selectedNode?: DirSvcMappingEntry,
};

class DestinationsView extends Component<Props, State> {
  handleSelectNode: Function;
  onChangeDestination: Function;
  onClickRemoveMapping: Function;
  onClickRevert: Function;
  onClickSaveMapping: Function;

  constructor() {
    super();
    this.handleSelectNode = this.handleSelectNode.bind(this);
    this.onChangeDestination = this.onChangeDestination.bind(this);
    this.onClickRemoveMapping = this.onClickRemoveMapping.bind(this);
    this.onClickRevert = this.onClickRevert.bind(this);
    this.onClickSaveMapping = this.onClickSaveMapping.bind(this);
  }

  state = { hasDeletes: false, selectedNode: undefined };

  componentDidMount() {
    this.props.getCatalogsAction();
    this.props.getRolesAction();
    this.props.getMappingsCommand();
  }

  async onChangeDestination(e) {
    await this.props.setDestinationAction(e.target.value);
    this.setState({ selectedNode: undefined });
  }

  handleSelectNode(entry) {
    this.setState({ selectedNode: entry });
  }

  async onClickSaveMapping(e) {
    const {
      saveMapping,
      intl: { formatMessage },
    } = this.props;
    await saveMapping(
      formatMessage(localizedStrings.LDAP_SYNC_NOTIFICATION),
      this.state.hasDeletes
    );
    this.setState({ hasDeletes: false });
  }

  async onClickRemoveMapping(e) {
    const { removeMappingEntry } = this.props;
    const { selectedNode } = this.state;
    if (selectedNode) {
      await removeMappingEntry(selectedNode);
      this.setState({
        hasDeletes: true,
        selectedNode: undefined,
      });
    }
  }

  async onClickRevert() {
    await this.props.getMappingsCommand();
    this.setState({ hasDeletes: false });
  }

  renderEntries(
    entriesForDestination: DirSvcMappingEntry[],
    showRoles: boolean
  ) {
    const { addOrUpdateMappingEntries, intl, roles } = this.props;
    return entriesForDestination.length ? (
      <ul>
        {entriesForDestination
          .sort((a, b) =>
            getUserName(a.sourceDN).localeCompare(getUserName(b.sourceDN))
          )
          .map(entry => (
            <DestinationNode
              addOrUpdateMappingEntries={addOrUpdateMappingEntries}
              entry={entry}
              intl={intl}
              isSelected={this.state.selectedNode === entry}
              key={entry.sourceDN}
              onSelectNode={this.handleSelectNode}
              roles={roles}
              showRoles={showRoles}
            />
          ))}
      </ul>
    ) : (
      <div className="full-page-message" data-test-name="no-mappings-message">
        <FormattedMessage
          {...localizedStrings.DIRECTORY_SERVICE_NO_MAPPINGS_LABEL}
        />
      </div>
    );
  }

  render() {
    const {
      catalogs,
      destination,
      intl: { formatMessage },
      isDirty,
      mappings,
      queryType,
    } = this.props;
    const { hasDeletes } = this.state;
    const mappingsAreDirty = isDirty || hasDeletes;
    const entriesForDestination = getMappingEntriesForDestination(
      mappings,
      destination
    );
    const showRoles = destination !== NO_CATALOGS_GUID;
    return (
      <div className="page column is-three-fifths">
        <div
          className={
            queryType === QUERY_TYPE_NAME
              ? 'dir-svc-mapping-form-section-expanded'
              : 'dir-svc-mapping-form-section'
          }
        >
          <div
            style={{
              margin: 'auto',
              width: '100%',
            }}
          >
            <LabeledField label={formatMessage(localizedStrings.CATALOG)}>
              <div className="select is-fullwidth">
                <select
                  component="input"
                  data-test-name="catalogSelect"
                  onChange={this.onChangeDestination}
                  value={destination}
                >
                  {catalogs.map(catalog => (
                    <option key={catalog.catalogId} value={catalog.catalogId}>
                      {catalog.name}
                    </option>
                  ))}
                  <option key={ALL_CATALOGS_GUID} value={ALL_CATALOGS_GUID}>
                    {formatMessage(localizedStrings.ALL_CATALOGS_GROUP)}
                  </option>
                  <option key={NO_CATALOGS_GUID} value={NO_CATALOGS_GUID}>
                    {formatMessage(localizedStrings.NO_CATALOGS)}
                  </option>
                </select>
              </div>
            </LabeledField>
          </div>
        </div>
        <div className="dir-svc-mapping-header">
          <div className="dir-svc-table-header">
            <div className="dir-svc-mapping-column dir-svc-cell dir-svc-cell-padded">
              <FormattedMessage
                {...localizedStrings.DIRECTORY_SERVICE_MAPPING_SOURCE_LABEL}
              />
            </div>
            {showRoles && (
              <div className="dir-svc-mapping-column dir-svc-cell dir-svc-cell-padded">
                <FormattedMessage {...localizedStrings.USER_ROLE} />
              </div>
            )}
          </div>
        </div>
        <div
          className="dir-svc-mapping-main-section"
          data-test-name="dir-svc-mappings-dest"
        >
          {this.renderEntries(entriesForDestination, showRoles)}
        </div>
        <div className="dir-svc-mapping-controls-section">
          <div className="buttons buttonsGroup is-pulled-left">
            <a
              className="button is-dark is-outlined"
              data-test-name="remove-mapping"
              onClick={this.onClickRemoveMapping}
              disabled={!this.state.selectedNode}
            >
              <FormattedMessage
                {...localizedStrings.DIRECTORY_SERVICE_REMOVE_MAPPING}
              />
              <span className="icon">
                <i className="fa fa-times-circle" />
              </span>
            </a>
          </div>
          <div className="buttons buttonsGroup is-pulled-right">
            <a
              className="button is-dark is-outlined"
              data-test-name="revert-changes"
              onClick={this.onClickRevert}
              disabled={!mappingsAreDirty}
            >
              <FormattedMessage {...localizedStrings.REVERT_CHANGES} />
              <span className="icon">
                <i className="fa fa-times-circle" />
              </span>
            </a>
            <a
              className="button is-primary"
              data-test-name="save-mappings"
              onClick={this.onClickSaveMapping}
              disabled={!mappingsAreDirty}
            >
              <FormattedMessage
                {...localizedStrings.DIRECTORY_SERVICE_SAVE_MAPPINGS}
              />
              <span className="icon">
                <i className="fa fa-check-circle fa-lg" />
              </span>
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  state => ({
    catalogs: getCatalogs(state),
    destination: getDirectoryServiceDestination(state),
    isDirty: getIsDirectoryServiceMappingsDirty(state),
    mappings: getDirectoryServiceMappings(state),
    queryType: getDirectoryServiceQueryType(state),
    roles: getRoles(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      addOrUpdateMappingEntries,
      getCatalogsAction,
      setDestinationAction,
      getMappingsCommand,
      getRolesAction,
      removeMappingEntry,
      saveMapping,
    })
)(injectIntl(DestinationsView));



// WEBPACK FOOTER //
// ./src/view/Pages/DirectoryServicesPage/DestinationsView.js